import useAnalyticsContext from '@hooks/useAnalyticsContext';
import { CtaButton } from '@tkww/orion-web-ds-action';
import { StatusBadge } from '@tkww/orion-web-ds-feedback';
import { TextBody } from '@tkww/orion-web-ds-text';
import { useAtomValue } from 'jotai';
import React, { useCallback, useMemo, useState } from 'react';
import {
	type Award,
	vendorAwardsAtom,
} from '../../jotai/storefront/awards-summary';
import { isQualifyingBowAward } from '../../pages/Storefront/components/award-items/constants';
import { BowBadgePaths } from '../../pages/Storefront/components/award-single-item/badge-svg-paths';
import { AwardBadgeSvg } from '../award-badge-svg/AwardBadgeSvg';
import { TooltipWrapper } from '../tooltip/TooltipWrapper';
import Styles from './BowAwardTile.scss';

const BOW_AWARD_DESCRIPTION =
	'The Knot independently assesses vendors for this award—it’s never self-reported. We base it on firsthand experiences from real couples and guests, and they must have a 4.5-star average across 7+ reviews in the qualifying year.';

const MAX_VISIBLE_YEARS = 3;

export const BowAwardTile = () => {
	const awards = useAtomValue(vendorAwardsAtom);
	const [isTooltipOpen, setIsTooltipOpen] = useState(false);
	const { track } = useAnalyticsContext();

	const handleTooltipOpen = useCallback(
		(open: boolean) => {
			setIsTooltipOpen((previousIsOpen) => {
				if (open && !previousIsOpen) {
					track({
						event: 'Tooltip Interaction',
						properties: { action: 'hover best of weddings' },
					});
				}
				return open;
			});
		},
		[track],
	);

	const qualifyingBowAwards: Award[] = useMemo(
		() => awards.filter(isQualifyingBowAward),
		[awards],
	);

	const count = qualifyingBowAwards.length;

	if (count === 0) return null;

	const isMultiYear = count >= 2;
	const firstAward = qualifyingBowAwards[0];

	const title = isMultiYear
		? `${count}-Time 'Best of Weddings'`
		: `${firstAward.displayYear} 'Best of Weddings'`;

	const overflow = count - MAX_VISIBLE_YEARS;
	const recentYears = qualifyingBowAwards.slice(
		0,
		overflow === 1 ? MAX_VISIBLE_YEARS + 1 : MAX_VISIBLE_YEARS,
	);

	return (
		<div className={Styles.tile}>
			<div className={Styles.badge}>
				<AwardBadgeSvg label="Best of Weddings Award">
					<BowBadgePaths />
				</AwardBadgeSvg>
			</div>

			<div className={Styles.content}>
				<TextBody
					bold
					color="dark"
					data-testid={
						isMultiYear
							? 'bow-award-multi-year-title'
							: 'bow-award-single-year-title'
					}
				>
					{title}
				</TextBody>

				{isMultiYear && (
					<div className={Styles.yearPills}>
						{recentYears.map((a) => (
							<StatusBadge key={a.id} styleType="callOut" size="sm">
								{a.displayYear}
							</StatusBadge>
						))}
						{overflow > 1 && (
							<StatusBadge
								styleType="callOut"
								size="sm"
								data-testid="bow-award-more-pill"
							>
								+{overflow} more
							</StatusBadge>
						)}
					</div>
				)}

				<TooltipWrapper
					arrowPosition="top"
					className={Styles.tooltipBody}
					containerClassName={Styles.tooltipWrapper}
					targetClassName={Styles.infoLink}
					targetProps={{
						'data-testid': 'bow-award-info-link',
						onMouseEnter: () => handleTooltipOpen(true),
						onMouseLeave: () => setIsTooltipOpen(false),
						onFocus: () => handleTooltipOpen(true),
						onBlur: () => setIsTooltipOpen(false),
					}}
					open={isTooltipOpen}
					onClick={handleTooltipOpen}
					target={
						<TextBody size="sm" color="subtle-dark" className={Styles.infoLink}>
							<CtaButton
								as="div"
								type="button"
								size="sm"
								styleType={'secondary'}
								className={Styles.iconButton}
								iconName="info"
								aria-label="Why were they selected?"
								data-testid="best-of-weddings-award-tile-tooltip-trigger"
							/>
							Why were they selected?
						</TextBody>
					}
				>
					<div className={Styles.tooltipContent}>
						<TextBody size="md" bold>
							Best of Weddings
						</TextBody>
						<TextBody size="sm">{BOW_AWARD_DESCRIPTION}</TextBody>
					</div>
				</TooltipWrapper>
			</div>
		</div>
	);
};
